import { useCallback, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { Button, Text, size, tags, useCheckBreakpoint, canUseDOM } from '@marriott/mi-ui-library';
import { useRouter } from 'next/router';
import { useStore } from '../../modules/store/cobrandStore';
import { SESSION_CONSTANTS, StringBoolean, CardType } from '../../modules/constants';
import { isMobile } from '../../modules/utils';
import { CtaComponentTypes } from './CtaComponent.types';
import { StyledCompareButton } from './CtaComponent.styles';

const toBoolean = (value?: StringBoolean | string | boolean): boolean =>
  value === true || value === 'true' || value === StringBoolean.TRUE;

export const CtaComponentConfig = {
  emptyLabel: 'Cta Component',
  isEmpty: () => true,
  resourceType: `/components/content/cta-component`,
};

export const CtaComp = (props: CtaComponentTypes) => {
  const {
    openInNewTab,
    linkText,
    linkUrl,
    ctaDescriptionMobile,
    ctaDescriptionDesktop,
    enableButtonByDefault,
    targetExperience,
    trackingProperties,
    comparisonTrayFlag,
    hideCtaDescription,
  } = props;

  const isExternalLink = toBoolean(openInNewTab);
  const linkType = isExternalLink ? 'external' : 'internal';
  const linkTarget = isExternalLink ? '_blank' : '_self';
  const enableCTAButton = toBoolean(enableButtonByDefault);
  const enableComparisonTray = toBoolean(props?.isComparisonTrayEnabled);

  const clickTrackValue = useMemo(() => {
    const position = trackingProperties?.trackingContentPosition ?? '';
    const description = trackingProperties?.trackingDescription ?? '';
    const additional = trackingProperties?.additionalTrackingVariables ?? '';
    return `${position}|${description}|${linkType}|${additional}`;
  }, [linkType, trackingProperties]);
  const { cobrandData, setCobrandData } = useStore(store => store);

  const isViewportL = useCheckBreakpoint('viewportL');
  const isMobileDevice = isMobile();
  const router = useRouter();

  // Utility to build URL params
  const buildUrlParams = useCallback(
    (cardParams: string) => {
      const params = new URLSearchParams();
      cardParams
        .split('&')
        .filter(Boolean)
        .forEach(param => {
          const [key, value] = param.split('=');
          if (key && value) params.set(key, value);
        });
      if (targetExperience) params.set('experience', targetExperience);
      if (router?.query) {
        Object.entries(router.query).forEach(([key, value]) => {
          if (key !== 'cardName') {
            params.set(key, value as string);
          }
        });
      }
      return `?${params.toString()}`;
    },
    [router.query, targetExperience]
  );

  // Session storage logic
  const setSelections = useCallback(() => {
    if (canUseDOM) {
      sessionStorage?.setItem(SESSION_CONSTANTS.COBRAND_DATA, JSON.stringify(cobrandData));
      sessionStorage?.setItem(SESSION_CONSTANTS.CREDIT_CARD_ENDPOINT, JSON.stringify(router?.asPath));
    }
  }, [cobrandData, router?.asPath]);

  // Effect: restore session data
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const cobrandDataFromSessionStorage = sessionStorage?.getItem(SESSION_CONSTANTS.COBRAND_DATA);
      if (cobrandDataFromSessionStorage) {
        setCobrandData(JSON.parse(cobrandDataFromSessionStorage));
        sessionStorage.removeItem(SESSION_CONSTANTS.COBRAND_DATA);
      }
      sessionStorage?.removeItem(SESSION_CONSTANTS.CREDIT_CARD_ENDPOINT);
    }
  }, [setCobrandData]);

  const compareButtonUrl = useMemo(() => {
    if (!cobrandData?.allCarouselCards?.length) {
      return '';
    }
    const cardParams = cobrandData.allCarouselCards
      .map((element: unknown, index: number) => `cardtype${index + 1}=${element}`)
      .join('&');
    return buildUrlParams(cardParams);
  }, [cobrandData?.allCarouselCards, buildUrlParams]);

  const selectedCardsUrl = useMemo(() => {
    const cardParams = cobrandData.selectedCards
      .filter((card: { checked?: boolean }) => card?.checked)
      .map((element: { cardname?: string }, index: number) =>
        element.cardname ? `cardtype${index + 1}=${element.cardname}` : ''
      )
      .filter(Boolean)
      .join('&');
    return buildUrlParams(cardParams);
  }, [cobrandData.selectedCards, buildUrlParams]);

  // Memo: enable card comparison and button text
  const { enableCardComparison, compareButtonText } = useMemo(() => {
    const checked = cobrandData?.selectedCards?.filter((card: { checked?: boolean }) => card?.checked) || [];
    const count = checked.length;
    return {
      enableCardComparison: count >= 2,
      compareButtonText: count > 0 ? ` (${count})` : '',
    };
  }, [cobrandData?.selectedCards]);

  // Button disabled logic
  const isButtonDisabled = !enableCardComparison && !enableCTAButton;

  const ctaCopy = useMemo(() => {
    if (!ctaDescriptionDesktop && !ctaDescriptionMobile) return undefined;
    return isViewportL && !isMobileDevice ? ctaDescriptionDesktop : ctaDescriptionMobile;
  }, [ctaDescriptionDesktop, ctaDescriptionMobile, isMobileDevice, isViewportL]);

  // Render logic inside return statement
  return (
    <StyledCompareButton data-testid="cobrand-CtaComponent" data-component-name="m-cobrand-CtaComponent">
      {/*
      enableCTAButton : Enables button by default, should be used in pages where comparison checkbox is not displayed in cardoverview components [Japan FOC]
      ENABLE_COMPARISON_TRAY : Displays comparison tray, Compare button to be displayed when tray functionality is disabled [ UK FOC]
      comparisonTrayFlag : only true when compare button is called inside comparison tray component. [ US FOC with comparison tray enabled]
       */}
      {(enableCTAButton || !enableComparisonTray || (enableComparisonTray && comparisonTrayFlag)) && (
        <div className="col-12 d-flex justify-content-center mt-3">
          <div className="text-center">
            {ctaCopy && !enableComparisonTray && !hideCtaDescription && (
              <Text
                fontSize={size.medium}
                element={tags.paragraph}
                copyText={ctaCopy}
                customClass={'t-subtitle-m  mb-3'}
              />
            )}
            {linkText && enableCTAButton && (
              <Button
                className={clsx('m-button-l compare-button mx-auto px-4')}
                isLink={true}
                href={`${linkUrl ?? ''}${compareButtonUrl}`}
                buttonCopy={enableComparisonTray ? linkText : linkText + compareButtonText}
                target={linkTarget}
                linkType={linkType}
                trackingProperties={trackingProperties}
                callback={setSelections}
                testId="comparebtn"
                custom_click_track_value={clickTrackValue}
              />
            )}
            {linkText && !enableCTAButton && (
              <Button
                className={clsx('m-button-l compare-button mx-auto px-4', isButtonDisabled && 'disabled')}
                isLink={true}
                href={`${linkUrl ?? ''}${selectedCardsUrl}`}
                buttonCopy={enableComparisonTray ? linkText : linkText + compareButtonText}
                target={linkTarget}
                linkType={linkType}
                isDisabled={isButtonDisabled}
                trackingProperties={{
                  ...trackingProperties,
                }}
                callback={setSelections}
                testId="comparebtn2"
                custom_click_track_value={clickTrackValue}
              />
            )}
          </div>
        </div>
      )}
    </StyledCompareButton>
  );
};

export const CtaComponent = (props: CtaComponentTypes) => (
  <EditableComponent config={CtaComponentConfig} {...props}>
    <CtaComp {...props} />
  </EditableComponent>
);
